import type { PublicEvent } from '../types';
import { getSiteAuthRuntime, getStoredToken } from './bliss-app-auth';

export interface UploadedEventImage {
  url: string;
  storageKey: string;
  name: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt?: string | null;
  kind?: 'cover' | 'gallery';
}

function getErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') {
    return fallback;
  }

  const source = payload as {
    error?: string | { message?: string };
    message?: string;
    data?: { message?: string };
  };

  if (typeof source.error === 'string' && source.error) {
    return source.error;
  }

  if (source.error && typeof source.error === 'object' && typeof source.error.message === 'string') {
    return source.error.message;
  }

  if (typeof source.message === 'string' && source.message) {
    return source.message;
  }

  if (source.data && typeof source.data.message === 'string' && source.data.message) {
    return source.data.message;
  }

  return fallback;
}

async function readResponseJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

async function requestEventsApi<T>(
  path: string,
  options: RequestInit = {},
  requireAuth = false,
): Promise<T> {
  const runtime = await getSiteAuthRuntime();
  const token = getStoredToken();
  const headers = new Headers(options.headers);
  const method = (options.method || 'GET').toUpperCase();
  const hasBody = options.body != null;

  if (requireAuth && !token) {
    throw new Error('You need to sign in again before changing event data.');
  }

  if (hasBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${runtime.apiBaseUrl}${path}`, {
    ...options,
    method,
    headers,
  });

  const payload = await readResponseJson(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'The events workspace could not reach the Bliss app API.'));
  }

  if (payload == null) {
    throw new Error('The events workspace received an unexpected response from the Bliss app API.');
  }

  const source = payload as { data?: T };
  return (source.data ?? payload) as T;
}

async function requestEventsUploadApi<T>(path: string, formData: FormData): Promise<T> {
  const runtime = await getSiteAuthRuntime();
  const token = getStoredToken();

  if (!token) {
    throw new Error('You need to sign in again before uploading event photos.');
  }

  const response = await fetch(`${runtime.apiBaseUrl}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const payload = await readResponseJson(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'The event image could not be uploaded right now.'));
  }

  const source = payload as { data?: T };
  return (source.data ?? payload) as T;
}

export async function listBlissEvents(includeAll = false): Promise<PublicEvent[]> {
  const query = includeAll ? '?include=all' : '';
  const result = await requestEventsApi<{ events: PublicEvent[] }>(`/events${query}`, {}, includeAll);
  return Array.isArray(result.events) ? result.events : [];
}

export async function createBlissEvent(event: PublicEvent): Promise<PublicEvent> {
  const result = await requestEventsApi<{ event: PublicEvent }>(
    '/events',
    {
      method: 'POST',
      body: JSON.stringify({ event }),
    },
    true,
  );
  return result.event;
}

export async function updateBlissEvent(event: PublicEvent): Promise<PublicEvent> {
  const result = await requestEventsApi<{ event: PublicEvent }>(
    `/events/${encodeURIComponent(event.id)}`,
    {
      method: 'PUT',
      body: JSON.stringify({ event }),
    },
    true,
  );
  return result.event;
}

export async function deleteBlissEvent(eventId: string): Promise<void> {
  await requestEventsApi(
    `/events/${encodeURIComponent(eventId)}`,
    {
      method: 'DELETE',
    },
    true,
  );
}

export async function replaceBlissEvents(events: PublicEvent[]): Promise<PublicEvent[]> {
  const result = await requestEventsApi<{ events: PublicEvent[] }>(
    '/events',
    {
      method: 'PUT',
      body: JSON.stringify({ events }),
    },
    true,
  );
  return Array.isArray(result.events) ? result.events : [];
}

export async function uploadBlissEventImage(
  eventId: string,
  file: File,
  kind: 'cover' | 'gallery',
): Promise<UploadedEventImage> {
  const formData = new FormData();
  formData.append('event_id', eventId);
  formData.append('kind', kind);
  formData.append('file', file);

  const result = await requestEventsUploadApi<{ image: UploadedEventImage }>('/events/upload', formData);
  return result.image;
}

export async function listBlissEventImages(): Promise<UploadedEventImage[]> {
  const result = await requestEventsApi<{ images: UploadedEventImage[] }>('/events/upload', {}, true);
  return Array.isArray(result.images) ? result.images : [];
}
